<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="true"
    :dismissableMask="false"
    :style="{ width: '500px' }"
    :header="isMutualFund ? 'Confirm Mutual Fund Purchase' : 'Confirm Stock Purchase'"
    class="buy-confirmation-dialog"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
  >
    <div v-if="isVisible" class="buy-confirm-content">
      <!-- Loading state for mobile -->
      <div v-if="!isDialogReady" class="dialog-loading">
        <div class="loading-spinner-small"></div>
        <p>Preparing order details...</p>
      </div>

      <!-- Main content - only show when ready -->
      <template v-else>
      <!-- Stock/MF Information -->
      <div class="stock-info-section">
        <div class="stock-header">
          <i :class="isMutualFund ? 'pi pi-briefcase stock-icon' : 'pi pi-chart-line stock-icon'"></i>
          <div class="stock-details">
            <h3 class="stock-name">{{ stockData.stockName }}</h3>
            <span class="stock-symbol">{{ stockData.stockSymbol }}</span>
          </div>
        </div>
        <div class="current-price">
          <span class="price-label">{{ isMutualFund ? 'Current NAV:' : 'Current Price:' }}</span>
          <span class="price-value">{{ formatCurrency(stockData.currentPrice) }}</span>
        </div>
      </div>

      <!-- Quantity Input (for Stocks) -->
      <div v-if="!isMutualFund" class="quantity-section">
        <label for="quantity-input" class="quantity-label">
          <i class="pi pi-shopping-cart"></i>
          Quantity (shares)
        </label>
        <InputNumber
          id="quantity-input"
          v-model="editableQuantity"
          :min="1"
          :max="10000"
          showButtons
          buttonLayout="horizontal"
          :step="1"
          :inputStyle="{ width: '100%' }"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
          class="quantity-input"
        />
        <small class="quantity-hint">
          Recommended: {{ stockData.quantity }} shares based on your portfolio allocation
        </small>
      </div>

      <!-- Lumpsum Amount Input (for Mutual Funds) -->
      <div v-if="isMutualFund" class="quantity-section">
        <label for="lumpsum-input" class="quantity-label">
          <i class="pi pi-wallet"></i>
          Lumpsum Amount
        </label>
        <InputNumber
          id="lumpsum-input"
          v-model="editableLumpsumAmount"
          :min="500"
          :max="50000"
          mode="currency"
          currency="INR"
          locale="en-IN"
          showButtons
          buttonLayout="horizontal"
          :step="500"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
          class="quantity-input"
        />
        <small class="quantity-hint">
          Recommended: {{ formatCurrency(stockData.lumpsumAmount || 500) }} (Range: ₹500 - ₹50,000)
        </small>
        <small class="units-display">
          Estimated units: {{ calculatedUnits.toFixed(2) }} units @ NAV {{ formatCurrency(stockData.currentPrice) }}
        </small>
      </div>

      <!-- Total Cost -->
      <div class="total-cost-section">
        <div class="cost-breakdown">
          <span class="cost-label">Total Investment:</span>
          <span class="cost-value">{{ formatCurrency(totalCost) }}</span>
        </div>
        <div class="cost-formula" v-if="!isMutualFund">
          {{ editableQuantity }} shares × {{ formatCurrency(stockData.currentPrice) }}
        </div>
        <div class="cost-formula" v-if="isMutualFund">
          {{ calculatedUnits.toFixed(2) }} units × {{ formatCurrency(stockData.currentPrice) }} NAV
        </div>
      </div>

      <!-- Important Notice -->
      <div class="notice-section">
        <i class="pi pi-info-circle"></i>
        <div class="notice-text">
          <p>You will be redirected to Zerodha Kite to complete this order.</p>
          <p class="notice-subtext">{{ isMutualFund ? 'Mutual fund order will be processed on next business day.' : 'Order will be placed at market price on next trading day.' }}</p>
        </div>
      </div>
    </template>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <ANButton
          label="Cancel"
          variant="secondary"
          @click="handleCancel"
          icon="pi pi-times"
        />
        <ANButton
          label="Confirm & Proceed to Kite"
          variant="primary"
          @click="handleConfirm"
          icon="pi pi-check"
          :disabled="isMutualFund ? editableLumpsumAmount < 500 : editableQuantity < 1"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  stockData: {
    type: Object,
    required: true,
    default: () => ({
      stockName: '',
      stockSymbol: '',
      currentPrice: 0,
      quantity: 0
    })
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Check if this is a mutual fund based on stockData
const isMutualFund = computed(() => props.stockData.isMutualFund || false)

const editableQuantity = ref(0)
const editableLumpsumAmount = ref(500)
const isDialogReady = ref(false)

// Calculate total cost based on investment type
const totalCost = computed(() => {
  if (isMutualFund.value) {
    return editableLumpsumAmount.value
  }
  return editableQuantity.value * (props.stockData.currentPrice || 0)
})

// Calculate units for mutual funds
const calculatedUnits = computed(() => {
  if (!isMutualFund.value || !props.stockData.currentPrice) return 0
  return editableLumpsumAmount.value / props.stockData.currentPrice
})

// Watch for visibility changes to initialize dialog
watch(isVisible, (visible) => {
  if (visible) {
    // Delay dialog content initialization slightly for mobile performance
    setTimeout(() => {
      isDialogReady.value = true
    }, 50)
  } else {
    isDialogReady.value = false
  }
}, { immediate: false })

// Watch for stockData changes to update quantity/lumpsum
watch(() => [props.stockData.quantity, props.stockData.lumpsumAmount, props.stockData.isMutualFund], ([quantity, lumpsumAmount, isMF]) => {
  if (isMF) {
    // For mutual funds, set lumpsum amount
    editableLumpsumAmount.value = lumpsumAmount || 500
  } else {
    // For stocks, set quantity
    editableQuantity.value = quantity || 0
  }
}, { immediate: true })

const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0.00'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

const handleConfirm = () => {
  if (isMutualFund.value) {
    // For mutual funds, emit lumpsum amount
    emit('confirm', {
      quantity: 0, // Not used for mutual funds
      lumpsumAmount: editableLumpsumAmount.value,
      totalCost: totalCost.value,
      stockSymbol: props.stockData.stockSymbol,
      stockName: props.stockData.stockName,
      currentPrice: props.stockData.currentPrice
    })
  } else {
    // For stocks, emit quantity
    emit('confirm', {
      quantity: editableQuantity.value,
      totalCost: totalCost.value,
      stockSymbol: props.stockData.stockSymbol,
      stockName: props.stockData.stockName,
      currentPrice: props.stockData.currentPrice
    })
  }
  isVisible.value = false
}

const handleCancel = () => {
  emit('cancel')
  isVisible.value = false
}
</script>

<style scoped>
.dialog-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
}

.loading-spinner-small {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.dialog-loading p {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

.buy-confirm-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stock-info-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px;
}

.stock-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.stock-icon {
  font-size: 2rem;
  color: #0ea5e9;
}

.stock-details {
  flex: 1;
}

.stock-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 4px 0;
}

.stock-symbol {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
}

.current-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid rgba(14, 165, 233, 0.2);
}

.price-label {
  font-size: 0.9rem;
  color: #475569;
  font-weight: 500;
}

.price-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #059669;
}

.quantity-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quantity-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--an-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.quantity-label i {
  color: #0ea5e9;
}

.quantity-input {
  width: 100%;
}

.quantity-hint {
  font-size: 0.8rem;
  color: #64748b;
  font-style: italic;
}

.units-display {
  font-size: 0.85rem;
  color: #059669;
  font-weight: 600;
  margin-top: 4px;
  display: block;
}

.total-cost-section {
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cost-breakdown {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cost-label {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.cost-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #059669;
}

.cost-formula {
  font-size: 0.85rem;
  color: #64748b;
  text-align: right;
}

.notice-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
}

.notice-section i {
  color: #d97706;
  font-size: 1.2rem;
  margin-top: 2px;
}

.notice-text {
  flex: 1;
}

.notice-text p {
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  color: #92400e;
  line-height: 1.4;
}

.notice-subtext {
  font-size: 0.8rem;
  color: #a16207;
  font-style: italic;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
}

/* Dialog Header Styling */
.buy-confirmation-dialog :deep(.p-dialog-header) {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  padding: 20px 24px;
}

.buy-confirmation-dialog :deep(.p-dialog-title) {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.buy-confirmation-dialog :deep(.p-dialog-content) {
  padding: 0;
}

/* InputNumber Styling */
.quantity-input :deep(.p-inputnumber) {
  width: 100%;
}

.quantity-input :deep(.p-inputnumber-input) {
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
}
</style>
